import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface HeroChipProps {
  icon: LucideIcon;
  children: ReactNode;
  /** Given one, the chip becomes a way through to whatever it counts. */
  to?: string;
  /** Named for a screen reader when the chip's own text is only a number. */
  label?: string;
}

/**
 * One fact about a screen, drawn over its banner's wash.
 *
 * Every console banner carries a row of these — the session, how many classes,
 * how many seats — and each page had grown its own copy of the same twelve
 * classes. One component, so a chip on the staff room looks like a chip on the
 * roster and neither can drift (CLAUDE.md §3).
 *
 * Blue throughout: the institute console has one accent and the banners are
 * painted from it (see `features/institute/theme.ts`). Written as literal
 * utilities rather than `text-accent`, because these sit on a fixed pale wash
 * that does not follow the theme, so a token that flipped would go white on
 * white in one of them.
 */
export function HeroChip({ icon: Icon, children, to, label }: HeroChipProps) {
  const skin =
    'inline-flex items-center gap-1.5 rounded-full border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-3 py-1 text-[11px] font-bold text-slate-700 dark:text-slate-200 backdrop-blur-sm';
  const mark = (
    <Icon className="h-3.5 w-3.5 shrink-0 text-blue-500 dark:text-blue-400" aria-hidden />
  );

  if (!to) {
    return (
      <span className={skin}>
        {mark}
        {children}
      </span>
    );
  }

  return (
    <Link
      to={to}
      aria-label={label}
      className={cn(
        skin,
        'transition-all hover:border-blue-400 hover:bg-white dark:hover:bg-slate-900',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50',
      )}
    >
      {mark}
      {children}
      <ArrowRight className="h-3 w-3 shrink-0 text-blue-500 dark:text-blue-400" aria-hidden />
    </Link>
  );
}
